import {
  Alert,
  Badge,
  Button,
  Group,
  Modal,
  Radio,
  Skeleton,
  Stack,
  Table,
  Text,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconAlertTriangle } from "@tabler/icons-react";
import { useState } from "react";
import { ConfirmActionModal, formatDateOnly } from "@how/ui";
import { usePerson, type PersonDetail } from "../queries";
import { useDismissDuplicate, useMergePeople, type DuplicatePair } from "./duplicate-queries";
import {
  CHOICE_FIELDS,
  defaultChoices,
  defaultKeep,
  describeMoved,
  describeReasons,
  differs,
  mergeInput,
  type ChoiceField,
  type Choices,
  type MergeSide,
} from "./merge";

const fullName = (side: MergeSide) => `${side.firstName} ${side.lastName}`;

function shown(side: MergeSide, field: ChoiceField): string {
  const value = side[field];
  if (value === null || value === "") return "None";
  return field === "birthdate" ? formatDateOnly(value) : value;
}

function listOf(values: readonly string[]): string {
  return values.length === 0 ? "None" : values.join(", ");
}

/** The parts of a record that move under fixed rules, shown so staff can see what they are combining. */
function summaryRows(detail: PersonDetail | undefined): Record<string, string> {
  if (detail === undefined) return {};
  const contacts = (method: "email" | "phone") =>
    listOf(detail.contacts.filter((c) => c.method === method).map((c) => c.value));
  return {
    Emails: contacts("email"),
    Phones: contacts("phone"),
    Households: listOf(detail.households.map((h) => h.name)),
    Tags: listOf(detail.tags.map((t) => t.name)),
    Notes: String(detail.notes.length),
    Added: new Date(detail.person.created_at).toLocaleDateString(),
  };
}

export interface MergeModalProps {
  readonly pair: DuplicatePair;
  readonly onClose: () => void;
}

/**
 * Two records side by side. Staff choose which record stays and, where the two disagree, which name and birthdate it
 * keeps; everything else follows the rules in ADR 0030, which the dialog states before anything happens.
 */
export function MergeModal({ pair, onClose }: MergeModalProps) {
  const initial = defaultKeep(pair.a, pair.b);
  const [keepId, setKeepId] = useState(initial.keep.id);
  const keep = keepId === pair.a.id ? pair.a : pair.b;
  const merge = keepId === pair.a.id ? pair.b : pair.a;
  const [choices, setChoices] = useState<Choices>(defaultChoices(initial.keep, initial.merge));
  const [confirming, setConfirming] = useState(false);

  const detailA = usePerson(pair.a.id);
  const detailB = usePerson(pair.b.id);
  const detailOf = (side: MergeSide) => (side.id === pair.a.id ? detailA.data : detailB.data);
  const loading = detailA.isPending || detailB.isPending;
  const bothSignIn =
    detailA.data?.person.auth_user_id != null && detailB.data?.person.auth_user_id != null;

  const mergePeople = useMergePeople();
  const dismiss = useDismissDuplicate();

  const chooseKeep = (id: string) => {
    setKeepId(id);
    const nextKeep = id === pair.a.id ? pair.a : pair.b;
    const nextMerge = id === pair.a.id ? pair.b : pair.a;
    setChoices(defaultChoices(nextKeep, nextMerge));
  };

  // The columns stay where they are when the kept record changes, so nothing moves under the pointer.
  const columns = [initial.keep, initial.merge];
  const summaries = new Map(columns.map((side) => [side.id, summaryRows(detailOf(side))]));
  const labels = Object.keys(summaries.get(initial.keep.id) ?? {});

  return (
    <>
      <Modal opened onClose={onClose} title="Review a possible duplicate" size="xl" radius="lg">
        <Stack gap="md">
          <Group gap="xs">
            {describeReasons(pair.reasons).map((reason) => (
              <Badge key={reason} variant="light" color="orange">
                {reason}
              </Badge>
            ))}
          </Group>

          <Table.ScrollContainer minWidth={560}>
            <Table verticalSpacing="sm" aria-label="The two records">
              <Table.Tbody>
                {/* The choice of record sits in the body, not a header row, so names keep their own case. */}

                <Table.Tr>
                  <Table.Td w="22%" />
                  {columns.map((side) => (
                    <Table.Td key={side.id}>
                      <Radio
                        checked={side.id === keepId}
                        onChange={() => {
                          chooseKeep(side.id);
                        }}
                        label={fullName(side)}
                        description={side.id === keepId ? "Stays" : "Merged into the other"}
                        aria-label={`Keep ${fullName(side)}`}
                      />
                    </Table.Td>
                  ))}
                </Table.Tr>

                {CHOICE_FIELDS.map(({ key, label }) => (
                  <Table.Tr key={key}>
                    <Table.Td>
                      <Text size="sm" fw={600}>
                        {label}
                      </Text>
                    </Table.Td>
                    {columns.map((side) => {
                      const which = side.id === keepId ? "keep" : "merge";
                      return (
                        <Table.Td key={side.id}>
                          {differs(keep, merge, key) ? (
                            <Radio
                              checked={choices[key] === which}
                              onChange={() => {
                                setChoices((current) => ({ ...current, [key]: which }));
                              }}
                              label={shown(side, key)}
                              aria-label={`${label}: use ${shown(side, key)}`}
                            />
                          ) : (
                            <Text size="sm">{shown(side, key)}</Text>
                          )}
                        </Table.Td>
                      );
                    })}
                  </Table.Tr>
                ))}
                {(loading ? ["Loading"] : labels).map((label) => (
                  <Table.Tr key={label}>
                    <Table.Td>
                      <Text size="sm" fw={600}>
                        {loading ? "" : label}
                      </Text>
                    </Table.Td>
                    {columns.map((side) => (
                      <Table.Td key={side.id}>
                        {loading ? (
                          <Skeleton h={14} />
                        ) : (
                          <Text size="sm">{summaries.get(side.id)?.[label]}</Text>
                        )}
                      </Table.Td>
                    ))}
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>

          <Text size="sm" c="dimmed">
            {fullName(merge)}&apos;s contacts, tags, households, notes and history move to{" "}
            {fullName(keep)}. Matching emails, phones and tags are kept once. Where both records
            have a value in the same custom field, the kept record&apos;s value stays. If either
            record has opted out of a kind of message, the opt-out is kept.
          </Text>

          {bothSignIn ? (
            <Alert color="orange" variant="light" icon={<IconAlertTriangle size={16} />}>
              Both people sign in to the member portal with their own account, so they cannot be
              merged. If they really are the same person, contact support.
            </Alert>
          ) : null}
          {mergePeople.error !== null || dismiss.error !== null ? (
            <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
              {(mergePeople.error ?? dismiss.error)?.message}
            </Alert>
          ) : null}

          <Group justify="space-between">
            <Button
              variant="default"
              loading={dismiss.isPending}
              onClick={() => {
                dismiss.mutate(
                  { a: pair.a.id, b: pair.b.id },
                  {
                    onSuccess: () => {
                      notifications.show({
                        color: "teal",
                        title: "Marked as different people",
                        message: `${fullName(pair.a)} and ${fullName(pair.b)} will not be suggested again.`,
                      });
                      onClose();
                    },
                  },
                );
              }}
            >
              Not a duplicate
            </Button>
            <Group gap="sm">
              <Button variant="default" onClick={onClose}>
                Cancel
              </Button>
              <Button
                disabled={loading || bothSignIn}
                onClick={() => {
                  setConfirming(true);
                }}
              >
                Merge
              </Button>
            </Group>
          </Group>
        </Stack>
      </Modal>

      <ConfirmActionModal
        opened={confirming}
        onClose={() => {
          setConfirming(false);
        }}
        title="Merge these records?"
        message={`${fullName(merge)} will be merged into ${fullName(keep)}. This cannot be undone.`}
        confirmLabel="Merge"
        danger
        loading={mergePeople.isPending}
        onConfirm={() => {
          const input = mergeInput(keep, merge, choices);
          mergePeople.mutate(input, {
            onSuccess: (moved) => {
              notifications.show({
                color: "teal",
                // The name the record has now, which the merge may just have changed.
                title: `Merged into ${input.firstName} ${input.lastName}`,
                message: describeMoved(moved),
              });
              setConfirming(false);
              onClose();
            },
            onError: () => {
              setConfirming(false);
            },
          });
        }}
      />
    </>
  );
}
